import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useState } from 'react';

const PERSONAL_EMAIL_DOMAINS = [
  'gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'aol.com',
  'icloud.com', 'mac.com', 'me.com', 'live.com', 'msn.com',
  'protonmail.com', 'mail.com', 'yandex.com', 'zoho.com'
];

const schema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  email: z.string()
    .email('Valid email is required')
    .refine((email) => {
      const domain = email.split('@')[1]?.toLowerCase();
      return !PERSONAL_EMAIL_DOMAINS.includes(domain);
    }, 'Business email required. Personal email addresses (Gmail, Yahoo, etc.) are not accepted'),
  phone: z.string().min(7, 'Valid phone number is required'),
  company: z.string().min(2, 'Company name is required'),
  employees: z.string().min(1, 'Number of employees is required'),
  industry: z.string().min(1, 'Industry is required'),
  annualRevenue: z.string().min(1, 'Annual revenue is required'),
  role: z.string().min(1, 'Role/title is required'),
});

const INDUSTRIES = [
  'Law Firm',
  'Accounting/Bookkeeping',
  'Consulting Firm',
  'Creative Agency',
  'Non-Profit',
  'Home Services',
  'Local B2C Business',
  'Other Professional Services'
];

const ROLES = [
  'Owner/Founder',
  'CEO/President',
  'COO',
  'Operations Manager/Director',
  'Other'
];

export const ApplicationForm = ({ onSubmit, isLoading }) => {
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm({ resolver: zodResolver(schema) });

  const [selectedEmployees, setSelectedEmployees] = useState('');
  const [selectedRevenue, setSelectedRevenue] = useState('');
  const [selectedIndustry, setSelectedIndustry] = useState('');
  const [selectedRole, setSelectedRole] = useState('');

  const submit = async (data) => {
    const formData = {
      ...data,
      employees: selectedEmployees,
      annualRevenue: selectedRevenue,
      industry: selectedIndustry,
      role: selectedRole,
      isTestPayment: false,
    };
    await onSubmit?.(formData);
  };

  return (
    <form
      data-testid="application-form"
      onSubmit={handleSubmit(submit)}
      className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6"
    >
      <div className="flex flex-col gap-2">
        <Label htmlFor="firstName" className="text-[#1c334a] font-medium text-sm">
          First Name <span className="text-red-600">*</span>
        </Label>
        <Input
          id="firstName"
          {...register('firstName')}
          data-testid="application-form-firstName-input"
          className="rounded-2xl border-[rgba(28,51,74,0.18)] focus-visible:ring-[#049aaa]"
          aria-invalid={!!errors.firstName}
          aria-describedby={errors.firstName ? 'firstName-error' : undefined}
        />
        {errors.firstName && (
          <span
            id="firstName-error"
            className="text-sm text-[#b00020]"
            data-testid="application-form-error"
          >
            {errors.firstName.message}
          </span>
        )}
      </div>

      <div className="flex flex-col gap-2">
        <Label htmlFor="lastName" className="text-[#1c334a] font-medium text-sm">
          Last Name <span className="text-red-600">*</span>
        </Label>
        <Input
          id="lastName"
          {...register('lastName')}
          data-testid="application-form-lastName-input"
          className="rounded-2xl border-[rgba(28,51,74,0.18)] focus-visible:ring-[#049aaa]"
          aria-invalid={!!errors.lastName}
          aria-describedby={errors.lastName ? 'lastName-error' : undefined}
        />
        {errors.lastName && (
          <span
            id="lastName-error"
            className="text-sm text-[#b00020]"
            data-testid="application-form-error"
          >
            {errors.lastName.message}
          </span>
        )}
      </div>

      <div className="flex flex-col gap-2">
        <Label htmlFor="email" className="text-[#1c334a] font-medium text-sm">
          Email <span className="text-red-600">*</span>
        </Label>
        <Input
          id="email"
          type="email"
          {...register('email')}
          data-testid="application-form-email-input"
          className="rounded-2xl border-[rgba(28,51,74,0.18)] focus-visible:ring-[#049aaa]"
          aria-invalid={!!errors.email}
          aria-describedby={errors.email ? 'email-error' : undefined}
        />
        {errors.email && (
          <span
            id="email-error"
            className="text-sm text-[#b00020]"
            data-testid="application-form-error"
          >
            {errors.email.message}
          </span>
        )}
      </div>

      <div className="flex flex-col gap-2">
        <Label htmlFor="phone" className="text-[#1c334a] font-medium text-sm">
          Phone <span className="text-red-600">*</span>
        </Label>
        <Input
          id="phone"
          type="tel"
          {...register('phone')}
          data-testid="application-form-phone-input"
          className="rounded-2xl border-[rgba(28,51,74,0.18)] focus-visible:ring-[#049aaa]"
          aria-invalid={!!errors.phone}
          aria-describedby={errors.phone ? 'phone-error' : undefined}
        />
        {errors.phone && (
          <span
            id="phone-error"
            className="text-sm text-[#b00020]"
            data-testid="application-form-error"
          >
            {errors.phone.message}
          </span>
        )}
      </div>

      <div className="flex flex-col gap-2">
        <Label htmlFor="company" className="text-[#1c334a] font-medium text-sm">
          Company <span className="text-red-600">*</span>
        </Label>
        <Input
          id="company"
          {...register('company')}
          data-testid="application-form-company-input"
          className="rounded-2xl border-[rgba(28,51,74,0.18)] focus-visible:ring-[#049aaa]"
          aria-invalid={!!errors.company}
          aria-describedby={errors.company ? 'company-error' : undefined}
        />
        {errors.company && (
          <span
            id="company-error"
            className="text-sm text-[#b00020]"
            data-testid="application-form-error"
          >
            {errors.company.message}
          </span>
        )}
      </div>

      <div className="flex flex-col gap-2">
        <Label htmlFor="employees" className="text-[#1c334a] font-medium text-sm">
          Number of Employees <span className="text-red-600">*</span>
        </Label>
        <Select
          value={selectedEmployees}
          onValueChange={(value) => {
            setSelectedEmployees(value);
            setValue('employees', value);
          }}
        >
          <SelectTrigger
            id="employees"
            data-testid="application-form-employees-input"
            className="rounded-2xl border-[rgba(28,51,74,0.18)] focus-visible:ring-[#049aaa] bg-white z-50"
          >
            <SelectValue placeholder="Select range" />
          </SelectTrigger>
          <SelectContent className="bg-white z-[100]">
            <SelectItem value="1-5">1-5 employees</SelectItem>
            <SelectItem value="6-10">6-10 employees</SelectItem>
            <SelectItem value="11-20">11-20 employees</SelectItem>
            <SelectItem value="20+">20+ employees</SelectItem>
          </SelectContent>
        </Select>
        {!selectedEmployees && errors.employees && (
          <span
            id="employees-error"
            className="text-sm text-[#b00020]"
            data-testid="application-form-error"
          >
            {errors.employees.message}
          </span>
        )}
      </div>

      <div className="flex flex-col gap-2">
        <Label htmlFor="industry" className="text-[#1c334a] font-medium text-sm">
          Industry <span className="text-red-600">*</span>
        </Label>
        <Select
          value={selectedIndustry}
          onValueChange={(value) => {
            setSelectedIndustry(value);
            setValue('industry', value);
          }}
        >
          <SelectTrigger
            id="industry"
            data-testid="application-form-industry-input"
            className="rounded-2xl border-[rgba(28,51,74,0.18)] focus-visible:ring-[#049aaa] bg-white z-50"
          >
            <SelectValue placeholder="Select industry" />
          </SelectTrigger>
          <SelectContent className="bg-white z-[100]">
            {INDUSTRIES.map((industry) => (
              <SelectItem key={industry} value={industry}>
                {industry}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {!selectedIndustry && errors.industry && (
          <span
            id="industry-error"
            className="text-sm text-[#b00020]"
            data-testid="application-form-error"
          >
            {errors.industry.message}
          </span>
        )}
      </div>

      <div className="flex flex-col gap-2">
        <Label htmlFor="annualRevenue" className="text-[#1c334a] font-medium text-sm">
          Annual Revenue <span className="text-red-600">*</span>
        </Label>
        <Select
          value={selectedRevenue}
          onValueChange={(value) => {
            setSelectedRevenue(value);
            setValue('annualRevenue', value);
          }}
        >
          <SelectTrigger
            id="annualRevenue"
            data-testid="application-form-annualRevenue-input"
            className="rounded-2xl border-[rgba(28,51,74,0.18)] focus-visible:ring-[#049aaa] bg-white z-50"
          >
            <SelectValue placeholder="Select range" />
          </SelectTrigger>
          <SelectContent className="bg-white z-[100]">
            <SelectItem value="<$500K">Less than $500K</SelectItem>
            <SelectItem value="$500K-$1M">$500K - $1M</SelectItem>
            <SelectItem value="$1M-$5M">$1M - $5M</SelectItem>
            <SelectItem value=">$5M">More than $5M</SelectItem>
          </SelectContent>
        </Select>
        {!selectedRevenue && errors.annualRevenue && (
          <span
            id="annualRevenue-error"
            className="text-sm text-[#b00020]"
            data-testid="application-form-error"
          >
            {errors.annualRevenue.message}
          </span>
        )}
      </div>

      <div className="flex flex-col gap-2 md:col-span-2">
        <Label htmlFor="role" className="text-[#1c334a] font-medium text-sm">
          Role/Title <span className="text-red-600">*</span>
        </Label>
        <Select
          value={selectedRole}
          onValueChange={(value) => {
            setSelectedRole(value);
            setValue('role', value);
          }}
        >
          <SelectTrigger
            id="role"
            data-testid="application-form-role-input"
            className="rounded-2xl border-[rgba(28,51,74,0.18)] focus-visible:ring-[#049aaa] bg-white z-50"
          >
            <SelectValue placeholder="Select your role" />
          </SelectTrigger>
          <SelectContent className="bg-white z-[100]">
            {ROLES.map((role) => (
              <SelectItem key={role} value={role}>
                {role}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {!selectedRole && errors.role && (
          <span
            id="role-error"
            className="text-sm text-[#b00020]"
            data-testid="application-form-error"
          >
            {errors.role.message}
          </span>
        )}
      </div>

      <div className="md:col-span-2 mt-4">
        <Button
          type="submit"
          data-testid="application-form-submit-button"
          disabled={isSubmitting || isLoading}
          className="w-full bg-[#d8e005] text-[#1c334a] rounded-2xl px-6 py-6 text-base hover:bg-transparent hover:text-[#1c334a] hover:border-2 hover:border-[#d8e005] font-black focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[rgba(4,154,170,0.35)] active:scale-[0.99] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmitting || isLoading 
            ? 'Processing...' 
            : 'Reserve My Spot ($1,250 deposit)'}
        </Button>
      </div>
    </form>
  );
};
